// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Row, Col } from 'antd/lib/grid';
import { LoadingOutlined, QuestionCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { ColumnFilterItem } from 'antd/lib/table/interface';
import Table from 'antd/lib/table';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import copy from 'copy-to-clipboard';
import Spin from 'antd/lib/spin';

import { Task, Job } from 'cvat-core-wrapper';
import { JobStage } from 'reducers';
import CVATTooltip from 'components/common/cvat-tooltip';
import UserSelector, { User } from './user-selector';
import { title } from 'process';
import { Checkbox, Modal } from 'antd';

import JobSublist from './job-sublist';

interface Props {
    jobs: any[];
    submissions: any[];
    users: any[];
    onUpdateJobs(jobInstances: Job[]): Promise<void>;
    fetching: boolean;
}

function JobListComponent(props: Props): JSX.Element {
    const { jobs, onUpdateJobs } = props;

    const history = useHistory();
    const [uniqueTasks, setUniqueTasks] = useState(false);

    const [selected, setSelected] = useState<any>({});

    function sorter(path: string) {
        return (obj1: any, obj2: any): number => {
            let currentObj1 = obj1;
            let currentObj2 = obj2;
            let field1: string | null = null;
            let field2: string | null = null;
            for (const pathSegment of path.split('.')) {
                field1 = currentObj1 && pathSegment in currentObj1 ? currentObj1[pathSegment] : null;
                field2 = currentObj2 && pathSegment in currentObj2 ? currentObj2[pathSegment] : null;
                currentObj1 = currentObj1 && pathSegment in currentObj1 ? currentObj1[pathSegment] : null;
                currentObj2 = currentObj2 && pathSegment in currentObj2 ? currentObj2[pathSegment] : null;
            }

            if (field1 && field2) {
                return field1.localeCompare(field2);
            }

            if (field1 === null) {
                return 1;
            }

            return -1;
        };
    }

    function collectUsers(path: string): ColumnFilterItem[] {
        return Array.from<string | null>(
            new Set(
                jobs.map((job: any) => {
                    if (job[path] === null) {
                        return null;
                    }

                    return job[path].username;
                }),
            ),
        ).map((value: string | null) => ({ text: value || 'Is Empty', value: value || false }));
    }

    const columns = [
        {
            title: 'Select',
            dataIndex: 'select',
            key: 'select',
            render: (jobId: number): JSX.Element => (
                <Checkbox
                    value={selected[jobId]}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelected({
                                ...selected,
                                [jobId]: true,
                            });
                        } else {
                            const selectedCopy = { ...selected };
                            delete selectedCopy[jobId];
                            setSelected(selectedCopy);
                        }
                    }}
                />
            ),
        },

        {
            title: 'Job',
            dataIndex: 'job',
            key: 'job',
            render: ({ jobId, taskId }: { jobId: number; taskId: number }): JSX.Element => (
                <div>
                    <Button
                        className='cvat-open-job-button'
                        type='link'
                        onClick={(e: React.MouseEvent): void => {
                            e.preventDefault();
                            history.push(`/tasks/${taskId}/jobs/${jobId}`);
                        }}
                        href={`/tasks/${taskId}/jobs/${jobId}`}
                    >
                        {`#${jobId}`}
                    </Button>
                </div>
            ),
        },
        {
            title: 'Task',
            dataIndex: 'task',
            key: 'task',
            render: (taskId: number): JSX.Element => (
                <div>
                    <Button
                        className='cvat-open-job-button'
                        type='link'
                        onClick={(e: React.MouseEvent): void => {
                            e.preventDefault();
                            history.push(`/tasks/${taskId}`);
                        }}
                        href={`/tasks/${taskId}`}
                    >
                        {`#${taskId}`}
                    </Button>
                </div>
            ),
        },
        {
            title: 'Phase',
            dataIndex: 'phase',
            key: 'phase',
            className: 'cvat-text-color',
            filters: [
                { text: 'phase0', value: 'phase0' },
                { text: 'phase1a', value: 'phase1a' },
                { text: 'phase1b', value: 'phase1b' },
                { text: 'phase2a', value: 'phase2a' },
                { text: 'phase2b', value: 'phase2b' },
                { text: 'phase3a', value: 'phase3a' },
                { text: 'phase3b', value: 'phase3b' },
                { text: 'phase4a', value: 'phase4a' },
                { text: 'phase4b', value: 'phase4b' },
            ],
            onFilter: (value: string | number | boolean, record: any) => record.phase === value,
        },
        {
            title: 'Assignee',
            dataIndex: 'assignee',
            key: 'assignee',
            className: 'cvat-job-item-assignee',
            render: (jobInstance: any): JSX.Element => (
                <UserSelector
                    className='cvat-job-assignee-selector'
                    value={jobInstance.assignee}
                    onSelect={(value: User | null): void => {
                        const doUpdate = () => {
                            const selectedJobs = jobs
                                .filter((job) => selected[job.id])
                                .concat(selected[jobInstance.id] ? [] : [jobInstance]);
                            selectedJobs.forEach((job: any) => {
                                if (job?.assignee?.id === value?.id) return;
                                job.assignee = value;
                            });
                            onUpdateJobs(selectedJobs);
                        };

                        if (!value) {
                            doUpdate();
                            return;
                        }

                        // Capacity and Disjointness warnings
                        const activeCount = jobs.filter(j => j.assignee && j.assignee.id === value.id && j.state !== 'completed').length;
                        const isAssigned = jobs.some(j => j.taskId === jobInstance.taskId && j.assignee && j.assignee.id === value.id && j.id !== jobInstance.id);
                        const jobToTask = new Map(jobs.map(j => [j.id, j.taskId]));
                        const hasSubmitted = props.submissions.some(s => s.user_id === value.id && jobToTask.get(s.job_id) === jobInstance.taskId);
                        
                        const hasConflict = isAssigned || hasSubmitted;
                        const hasCapacityWarning = activeCount >= 3;

                        if (hasConflict || hasCapacityWarning) {
                            let bypass = false;
                            const modalRef = Modal.confirm({
                                title: 'Safety Checks Triggered',
                                width: 500,
                                content: (
                                    <div>
                                        <div style={{ marginBottom: 16 }}>
                                            {hasConflict && <p style={{ color: 'red', fontWeight: 'bold' }}>[!] Conflict: Already worked on Task #{jobInstance.taskId}</p>}
                                            {hasCapacityWarning && <p style={{ color: '#d46b08', fontWeight: 'bold' }}>[!] Capacity: User already has {activeCount} active jobs (Limit: 3)</p>}
                                        </div>
                                        <Checkbox 
                                            onChange={(e) => {
                                                bypass = e.target.checked;
                                                modalRef.update({
                                                    okButtonProps: {
                                                        disabled: !bypass,
                                                        children: 'Force Assignment',
                                                    }
                                                });
                                            }}
                                        >
                                            Bypass Safety Checks (Disjointness, Frame Limits)
                                        </Checkbox>
                                    </div>
                                ),
                                okText: 'Force Assignment',
                                okButtonProps: {
                                    disabled: true,
                                },
                                cancelText: 'Cancel',
                                onOk: () => {
                                    doUpdate();
                                }
                            });
                            return;
                        }

                        doUpdate();
                    }}
                />
            ),
            sorter: sorter('assignee.assignee.username'),
            filters: collectUsers('assignee'),
            onFilter: (value: string | number | boolean, record: any) =>
                (record.assignee.assignee?.username || false) === value,
        },
        Table.EXPAND_COLUMN,
    ];

    let completed = 0;
    const tasks: { [jobid: number]: boolean } = {};
    const data = jobs
        .filter((job: any) => {
            if (uniqueTasks) {
                const toInclude = !tasks[job.taskId];
                tasks[job.taskId] = true;
                return toInclude;
            } else {
                return true;
            }
        })
        .reduce((acc: any[], job: any) => {
            if (job.stage === 'acceptance') {
                completed++;
            }

            acc.push({
                select: job.id,
                key: job.id,
                job: { jobId: job.id, taskId: job.taskId },
                task: job.taskId,
                // state: job,
                // stage: job,
                assignee: job,
                phase: job.phase,
            });

            return acc;
        }, []);

    return (
        <div className='cvat-task-job-list'>
            <Row justify='space-between' align='middle'>
                <Col>
                    <Text className='cvat-text-color cvat-jobs-header'> Jobs </Text>
                </Col>
                <Col>
                    <Checkbox
                        onChange={(e) => {
                            setUniqueTasks(e.target.checked);
                        }}
                        checked={uniqueTasks}
                    >
                        Show Unique Tasks
                    </Checkbox>
                </Col>
                <Col>
                    <Text className='cvat-text-color'>{`${completed} of ${data.length} jobs`}</Text>
                </Col>
            </Row>
            <Table
                className='cvat-task-jobs-table'
                rowClassName={() => 'cvat-task-jobs-table-row'}
                columns={columns}
                dataSource={data}
                size='small'
                expandable={{
                    expandedRowRender: (record: any) => (
                        <JobSublist
                            jobId={record.job.jobId}
                            submissions={props.submissions}
                            taskId={record.job.taskId}
                            users={props.users}
                        />
                    ),
                    rowExpandable: (record: any) => record.phase !== 'phase0',
                }}
                loading={props.fetching}
                loadingIndicator={<Spin size='large' className='cvat-spinner' />}
            />
        </div>
    );
}

export default React.memo(JobListComponent);
